import React, { RefObject } from 'react';
import CoreTooltip from 'shared/CoreTooltip';
import styled from 'styled-components';
import style from 'styles/styled-components/style';

export interface IHeaderCell {
	_width: number;
	_margin?: string;
	_padding?: string;
	_ref?:
		| ((instance: HTMLDivElement | null) => void)
		| React.RefObject<HTMLDivElement>
		| null;
	resizer_ref?:
		| ((instance: HTMLDivElement | null) => void)
		| React.RefObject<HTMLDivElement>
		| null;
	children?: string | JSX.Element | (string | JSX.Element)[];
	tooltip_text?: string;
	_onMouseEnter?: React.MouseEventHandler<HTMLDivElement>;
	_onMouseLeave?: React.MouseEventHandler<HTMLDivElement>;
	_onClick?: React.MouseEventHandler<HTMLDivElement>;
}

export interface IHeaderNumberCell extends IHeaderCell {
	_onMouseEnter?: React.MouseEventHandler<HTMLDivElement>;
	_onMouseLeave?: React.MouseEventHandler<HTMLDivElement>;
	_onClick?: React.MouseEventHandler<HTMLDivElement>;
}

const TableHeaderCell = ({
	_width,
	_margin,
	_padding,
	_ref,
	resizer_ref,
	children,
	tooltip_text,
	_onMouseEnter,
	_onMouseLeave,
	_onClick,
}: IHeaderCell) => {
	return (
		<>
			<TableHeaderCellBox
				_width={_width}
				_margin={_margin}
				_padding={_padding ?? `0 0 0 16px`}
				ref={_ref}
				flex="start"
				pointer={_onClick ? true : false}
				onMouseEnter={_onMouseEnter}
				onMouseLeave={_onMouseLeave}
				onClick={_onClick}
			>
				<CoreTooltip
					_width="240px"
					text={tooltip_text}
					direction="down"
					_disabled={tooltip_text === undefined}
				>
					<style.Grid flex="start">
						{children as string | JSX.Element | (string | JSX.Element)[]}
					</style.Grid>
				</CoreTooltip>
			</TableHeaderCellBox>

			<Resizer ref={resizer_ref}>
				<span />
			</Resizer>
		</>
	);
};

const Number = ({
	_width,
	_margin,
	_padding,
	_ref,
	resizer_ref,
	_onMouseEnter,
	_onMouseLeave,
	_onClick,
	children,
	tooltip_text,
}: IHeaderNumberCell) => {
	return (
		<>
			<TableHeaderCellBox
				_width={_width}
				_margin={_margin}
				_padding={`0 8px 0 0`}
				ref={_ref}
				flex="end"
				pointer={_onClick ? true : false}
				onMouseEnter={_onMouseEnter}
				onMouseLeave={_onMouseLeave}
				onClick={_onClick}
			>
				<CoreTooltip
					_width="240px"
					text={tooltip_text}
					direction="down"
					_disabled={tooltip_text === undefined}
				>
					<style.Grid flex="start">
						{children as string | JSX.Element | (string | JSX.Element)[]}
					</style.Grid>
				</CoreTooltip>
			</TableHeaderCellBox>
			<Resizer ref={resizer_ref}>
				<span />
			</Resizer>
		</>
	);
};

const Etc = ({
	_width,
	_margin,
	_ref,
	resizer_ref,
	children,
	tooltip_text,
}: IHeaderCell) => {
	return (
		<>
			<TableHeaderCellBox _width={_width} _margin={_margin} ref={_ref} flex="center">
				<CoreTooltip
					_width="240px"
					text={tooltip_text}
					direction="down"
					_disabled={tooltip_text === undefined}
				>
					<span>{children as string | JSX.Element | (string | JSX.Element)[]}</span>
				</CoreTooltip>
			</TableHeaderCellBox>
			<Resizer ref={resizer_ref}>
				<span />
			</Resizer>
		</>
	);
};

const None = ({ _width, _margin, _ref, children }: IHeaderCell) => {
	return (
		<>
			<TableHeaderCellBox
				_width={_width}
				_margin={_margin}
				ref={_ref}
				flex="start"
				is_bold={true}
			>
				{children}
			</TableHeaderCellBox>
		</>
	);
};

TableHeaderCell.Common = TableHeaderCell;
TableHeaderCell.Number = Number;
TableHeaderCell.Etc = Etc;
TableHeaderCell.None = None;

export const TableHeaderCellBox = styled.div<{
	_width: number;
	flex?: 'start' | 'end' | 'center';
	_margin?: string;
	_padding?: string;
	pointer?: boolean;
	is_bold?: boolean;
}>`
	width: ${({ _width }) => `${_width}px`};
	height: 100%;
	margin: ${({ _margin }) => _margin};
	padding: ${({ _padding }) => _padding};
	overflow: hidden;

	${({ is_bold, theme }) => (is_bold ? theme.typo.body.span_bold : theme.typo.body.span)};
	color: ${({ theme }) => theme.colors.gray.gray80_text};

	${({ theme, flex }) => (!flex || flex === 'end') && theme.common.flexEnd};
	${({ theme, flex }) => flex === 'start' && theme.common.flexStart};
	${({ theme, flex }) => flex === 'center' && theme.common.flexCenter};

	${({ theme }) => theme.common.ellipsis};

	flex-grow: 0;
	flex-shrink: 0;
	cursor: ${({ pointer }) => (pointer ? `pointer` : `default`)};
	user-select: none;
`;

const Resizer = styled.div.attrs({
	className: 'resizer',
})<{ is_first?: boolean }>`
	width: 16px;
	height: 100%;
	${({ theme }) => theme.common.flexCenter};
	cursor: auto;

	${({ is_first }) => is_first && `margin-right: 64px`};

	&:hover {
		cursor: col-resize;
	}

	& > span {
		width: 1px;
		height: 16px;
		background-color: ${({ theme }) => theme.colors.gray.gray15};
		transition: 0.2s;
	}

	&:hover > span {
		width: 4px;
		height: 24px;
		background-color: ${({ theme }) => theme.colors.primary.primary50_key};
	}
`;

export default TableHeaderCell;
